import { Object as FabricObject } from 'fabric/fabric-impl';
import { Scheme } from '../types';

/**
 * Зависимости показа. Принимаются извне, чтобы презентер тестировался без настоящей
 * канвы Fabric и оставался движком: без React, без стора и без знания фич.
 */
export interface BoardPresenterDeps {
  /** Объекты, которые сейчас на канве. */
  getObjects: () => FabricObject[];
  /** Затухание объектов; резолвится, когда анимация закончилась. */
  fadeOut: (objects: FabricObject[]) => Promise<unknown>;
  /** Загрузка схемы табло на канву. Отменить её нельзя — только дождаться. */
  load: (scheme: Scheme) => Promise<unknown>;
  /** Полная очистка канвы. */
  clear: () => void;
  /**
   * Передаёт наружу признак «идущая отрисовка устарела» — для асинхронных колбэков,
   * которых презентер не держит в руках: загрузка картинки в Replacer резолвится уже
   * после того, как на канву поехало другое табло.
   */
  setStaleCheck?: (isStale: () => boolean) => void;
}

/** Пропуск текущего поколения показа: по нему асинхронный код решает, продолжать ли. */
export interface BoardShowToken {
  /** false — показ отменён более свежим `show()` или `clear()`: продолжать нельзя. */
  readonly isCurrent: boolean;
}

export interface BoardShowOptions {
  /** Схема табло. Не задана — показывать нечего. */
  scheme?: Scheme;
  /** Очистить канву после затухания, если схемы нет (табло выключено). */
  clearOnEmpty?: boolean;
  /** Вызывается сразу после затухания, до загрузки. */
  onFaded?: (token: BoardShowToken) => void;
  /** Вызывается перед загрузкой; `false` — загрузку не начинать. */
  beforeLoad?: (token: BoardShowToken) => boolean | void;
  /** Вызывается после загрузки схемы, если поколение всё ещё актуально. */
  onLoaded?: (token: BoardShowToken) => void;
}

/**
 * Владелец показа табло. Показ — цепочка из затухания и асинхронной загрузки, и без
 * владельца две смены табло внутри секунды накладываются на одной канве: побеждает та
 * загрузка, которая резолвилась последней, а не та, которую запросили последней.
 *
 * Каждый `show()` увеличивает поколение. Всё, что резолвится из прошлого поколения,
 * отбрасывается; загрузка, которую отменить уже нельзя, дожидается — свежие объекты
 * ложатся на канву последними.
 */
export class BoardPresenter {
  private deps: BoardPresenterDeps | null = null;
  private generation = 0;
  private pendingLoad: Promise<unknown> | null = null;

  init(deps: BoardPresenterDeps) {
    this.deps = deps;
  }

  get isInit(): boolean {
    return this.deps !== null;
  }

  /** Отменяет текущий показ, не трогая канву. */
  cancel(): void {
    this.startGeneration();
  }

  /** Затухание и очистка канвы: табло выключено. */
  clear(): Promise<void> {
    return this.show({ clearOnEmpty: true });
  }

  async show(options: BoardShowOptions = {}): Promise<void> {
    const deps = this.deps;
    if (!deps) {
      console.error({ event: 'Using the BoardPresenter module before initialization' });
      return;
    }

    const { scheme, clearOnEmpty = false, onFaded, beforeLoad, onLoaded } = options;

    const token = this.startGeneration();
    deps.setStaleCheck?.(() => !token.isCurrent);

    await deps.fadeOut(deps.getObjects());
    if (!token.isCurrent) return;

    onFaded?.(token);
    if (!token.isCurrent) return;

    if (!scheme) {
      clearOnEmpty && deps.clear();
      return;
    }

    if (beforeLoad?.(token) === false) return;
    if (!token.isCurrent) return;

    // Прошлая загрузка чистит канву и добавляет свои объекты в момент резолва, отменить
    // её нельзя. Ждём её и грузим поверх — иначе она затрёт уже показанное свежее табло.
    if (this.pendingLoad) {
      await this.pendingLoad.catch(() => undefined);
      if (!token.isCurrent) return;
    }

    const load = deps.load(scheme);
    this.pendingLoad = load;
    try {
      await load;
    } finally {
      if (this.pendingLoad === load) {
        this.pendingLoad = null;
      }
    }
    if (!token.isCurrent) return;

    onLoaded?.(token);
  }

  private startGeneration(): BoardShowToken {
    const generation = ++this.generation;
    const presenter = this;
    return {
      get isCurrent() {
        return presenter.generation === generation;
      },
    };
  }
}

export default BoardPresenter;
